import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface EquipmentPickup {
  id: string;
  time_entry_id: string;
  user_id: string;
  project_id: string;
  description: string | null;
  photo_count: number;
  created_at: string;
  employee_name: string;
  project_name: string;
}

const getPin = (): string => {
  const pin = localStorage.getItem('manager_pin') || localStorage.getItem('employee_pin');
  if (!pin) throw new Error('PIN manquant');
  return pin;
};

export const useEquipmentPickups = () => {
  const [pickups, setPickups] = useState<EquipmentPickup[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const fetchPending = useCallback(async () => {
    try {
      const pin = getPin();
      const { data, error } = await supabase.rpc('pin_list_pending_equipment_pickups', {
        p_pin: pin,
      });
      if (error) throw error;
      setPickups((data || []) as EquipmentPickup[]);
    } catch (error) {
      console.error('Erreur chargement équipements:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const createPickup = async (
    timeEntryId: string,
    description: string | null,
    photoCount: number
  ): Promise<string | null> => {
    try {
      const pin = getPin();
      const { data, error } = await supabase.rpc('pin_create_equipment_pickup', {
        p_pin: pin,
        p_time_entry_id: timeEntryId,
        p_description: description || null,
        p_photo_count: photoCount,
      });
      if (error) throw error;
      return data as string;
    } catch (error) {
      console.error('Erreur création signalement:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de signaler les équipements',
        variant: 'destructive',
      });
      return null;
    }
  };

  const resolvePickup = async (pickupId: string): Promise<boolean> => {
    try {
      const pin = getPin();
      const { error } = await supabase.rpc('pin_resolve_equipment_pickup', {
        p_pin: pin,
        p_pickup_id: pickupId,
      });
      if (error) throw error;
      toast({ title: '✅ Équipement marqué comme récupéré' });
      await fetchPending();
      return true;
    } catch (error) {
      console.error('Erreur résolution:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de marquer comme récupéré',
        variant: 'destructive',
      });
      return false;
    }
  };

  const uploadPhotos = async (pickupId: string, files: File[]): Promise<void> => {
    for (const file of files) {
      const fileId = crypto.randomUUID();
      const path = `${pickupId}/${fileId}.jpg`;
      const { error } = await supabase.storage
        .from('equipment-photos')
        .upload(path, file, { contentType: file.type });
      if (error) {
        console.error(`Erreur upload photo ${fileId}:`, error);
      }
    }
  };

  const getPhotoUrls = async (pickupId: string): Promise<string[]> => {
    const { data, error } = await supabase.storage
      .from('equipment-photos')
      .list(pickupId);
    if (error || !data) return [];
    return data.map(file => {
      const { data: urlData } = supabase.storage
        .from('equipment-photos')
        .getPublicUrl(`${pickupId}/${file.name}`);
      return urlData.publicUrl;
    });
  };

  return { pickups, loading, fetchPending, createPickup, resolvePickup, uploadPhotos, getPhotoUrls };
};
